import { useMutation, useQuery } from '@apollo/client'
import { Button, Checkbox, Divider, Form, Input, message, Modal } from 'antd'
import { useForm } from 'antd/lib/form/Form'
import dayjs from 'dayjs'
import gql from 'graphql-tag'
import { Maybe } from 'graphql/jsutils/Maybe'
import React, { useCallback, useEffect } from 'react'
import { DEVIS_FOURNISSEUR_MUTATION } from '../../apollo/mutation/devisfournisseur.mutation'
import { EVENEMENT_ADD_MUTATION } from '../../apollo/mutation/evenement.mutation'
import { FICHIER_QUERY } from '../../apollo/mutation/fichier.mutation'
import { EVENEMENTS } from '../../apollo/query/demande.query'
import { C_S_QUERY } from '../../apollo/query/personne.query'
import { AWS_S3_RESIZED_URL, AWS_S3_URL } from '../../constants'
import { formatMembreCSOptions, useImperativeQuery } from '../../helper'
import { useAuth, useQueryCopro, useUpdateKanban } from '../../lib/hooks'
import {
  D4_Conseilsyndical,
  D4_Devisfournisseur,
  D4_Devisfournisseur_Set_Input,
  D4_Evenementdemande_Set_Input,
  D4_Fichier,
  D4_Fn_View_Demande,
  D4_Fn_View_Evenements,
  D4_Personne,
  D4_Societe,
} from '../../types'
import MsGraphAPI, { useMsGraphAPI } from '../Courriel/ms-graph.api'
import Thumbnail from '../Thumbnail'
import DebounceSelect from './DebounceSelect'

const COURRIEL_DESTINATAIRES_QUERY = gql`
  query CourrielDestinataires($trg: String!, $search: String!) {
    gestionnaires: d4_personne(
      where: {
        _and: [
          { dtype: { _eq: "Gestionnaire" } }
          { mail1: { _neq: "" } }
          { _or: [{ nom: { _ilike: $search } }, { prenom: { _ilike: $search } }, { mail1: { _ilike: $search } }] }
        ]
      }
      limit: 10
    ) {
      id
      nom
      prenom
      mail1
    }
    personnes: d4_personne(
      where: {
        _and: [
          { coproprietaire: { lot_coproprietaires: { lot: { copropriete: { trigramme: { _eq: $trg } } } } } }
          { mail1: { _neq: "" } }
          { _or: [{ nom: { _ilike: $search } }, { prenom: { _ilike: $search } }, { mail1: { _ilike: $search } }] }
        ]
      }
      limit: 10
    ) {
      id
      nom
      prenom
      mail1
    }
    fournisseurs: d4_societe(
      where: {
        _and: [
          { mail1: { _neq: "" } }
          { _or: [{ mail1: { _ilike: $search } }, { raisonsociale: { _ilike: $search } }] }
        ]
      }
      limit: 10
    ) {
      id
      raisonsociale
      mail1
    }
  }
`

interface Props {
  onOk: (() => void) | undefined
  onCancel: (() => void) | undefined
  visible: boolean
  evenements: Maybe<D4_Fn_View_Evenements[]>
  demande: D4_Fn_View_Demande
  fichierId: number
}

// `${demande.description} (${demande.id})`
interface SendMailData {
  body: string
  subject: string
  evenements: D4_Fn_View_Evenements[]
  to: string[]
  cc: string[]
}

// let isImg = (texte: string) => /\.(gif|jpe?g|tiff?|png|webp|bmp)$/i.test(texte ?? '')
let isPdf = (texte: string) => /\.(pdf)$/i.test(texte ?? '')

const sendEmail = async ({ subject, body, evenements, to, cc }: SendMailData) => {
  const data = {
    message: {
      subject: subject,
      body: {
        contentType: 'html',
        content:
          body.replaceAll('\n', '<br />') +
          '<br/><br/>' +
          evenements
            ?.map(
              (ev) =>
                `<div>${ev.auteurnom} - ${ev.auteurprenom}<br />${
                  ev.fichier
                    ? `<a href="${AWS_S3_URL}${ev.texte}" target="_blank" >
                    <img width="150px" src="${AWS_S3_RESIZED_URL}resized-${ev.texte}${
                        isPdf(ev.texte ?? '') ? '.png' : ''
                      }" />
                  </a>`
                    : ev.texte
                }</div><hr><br />`
            )
            .join(''),
      },
      toRecipients: to?.map((personne) => ({ emailAddress: { address: personne.trim().toLocaleLowerCase() } })) ?? [],
      ccRecipients: cc?.map((personne) => ({ emailAddress: { address: personne.trim().toLocaleLowerCase() } })) ?? [],
    },
    saveToSentItems: 'true',
  }

  const res = await MsGraphAPI.sendEmail({ data })
  return res
}

const ModalEnvoyerDevis: React.FC<Props> = ({ visible, onOk, onCancel, evenements, demande, fichierId }) => {
  const [form] = useForm()
  const {
    user: {
      personne: { nom, prenom },
    },
  } = useAuth()
  const [createEvenement] = useMutation<{}, { data: D4_Evenementdemande_Set_Input }>(EVENEMENT_ADD_MUTATION)

  const search = useImperativeQuery<
    { personnes: D4_Personne[]; fournisseurs: D4_Societe[]; gestionnaires: D4_Personne[] },
    { trg: string; search: string }
  >(COURRIEL_DESTINATAIRES_QUERY)

  const { data: cs } = useQuery<{ d4_conseilsyndical: D4_Conseilsyndical[] }, { trg: string }>(C_S_QUERY, {
    variables: { trg: demande.copropriete?.trigramme ?? '' },
  })

  //{etatkanbanid: 3768210}
  const { updateKanban } = useUpdateKanban(demande.id)

  const [copro] = useQueryCopro(demande.coproprieteid)
  const [updateDevisFournisseur] = useMutation<
    { devisfournisseur: D4_Devisfournisseur },
    { id: number; data: D4_Devisfournisseur_Set_Input }
  >(DEVIS_FOURNISSEUR_MUTATION)

  useEffect(() => {
    if (cs) {
      const [, values] = formatMembreCSOptions(cs?.d4_conseilsyndical ?? [])
      form.setFieldsValue({ to: values.map((val: any) => val?.mail1) })
    }
  }, [cs, cs?.d4_conseilsyndical, form])

  const { data: fichier } = useQuery<{ fichier: D4_Fichier; id: number }>(FICHIER_QUERY, {
    variables: { id: fichierId },
    skip: !fichierId,
  })

  useMsGraphAPI()

  const handleFinish = useCallback(
    /**
     * changer l'etat kanban, mettre date envoi sur now
     */
    async (values: any) => {
      const data: SendMailData = {
        to: values.to,
        cc: values.cc,
        evenements: evenements?.filter((ev) => values.evt.includes(ev.id)) ?? ([] as D4_Fn_View_Evenements[]),
        subject: values.subject,
        body: values.body,
      }

      try {
        const res = await sendEmail(data)
        if (fichier?.fichier) {
          await updateDevisFournisseur({
            variables: { id: fichier?.fichier.devisfournisseur_id, data: { dateenvoiauconseilsyndical: dayjs() } },
          })
        }
        await updateKanban(3768210, { label: '', value: 3768210, etatkanbanid: 3768210 })

        if (res.success) {
          message.success('Le mail a bien été envoyé')
          form.resetFields()
          createEvenement({
            variables: {
              data: {
                demande_id: demande.id,
                texte: `Envoi du mail\n\n<strong>De : ${nom} ${prenom}\nA : ${values.to?.join(', ') ?? ''}\nCc : ${
                  values.cc?.join(', ') ?? ''
                }\nObjet : ${values.subject}</strong>\n\n${values.body}`,
              },
            },
            refetchQueries: [
              {
                query: EVENEMENTS,
                variables: { id: demande.id },
              },
            ],
          })
          onOk?.()
        } else {
          message.error("Une erreur est survenue lors de l'envoi du mail")
        }
      } catch (error) {}
    },
    [
      createEvenement,
      demande.id,
      evenements,
      fichier?.fichier,
      form,
      nom,
      onOk,
      prenom,
      updateDevisFournisseur,
      updateKanban,
    ]
  )

  const handleSearch = useCallback(
    async (v: string): Promise<any[]> => {
      let nextOptions = []
      if (!v || v.length < 3) return Promise.resolve([])
      const res = await search({ trg: copro?.trigramme ?? '', search: '%' + v + '%' })

      res.data.gestionnaires.length > 0 &&
        nextOptions.push({
          key: 'gestionnaires',
          label: 'Gestionnaires',
          options: res.data.gestionnaires.map((ges) => ({
            value: ges.mail1,
            label: ges.nom + ' ' + ges.prenom,
            key: ges.id,
          })),
        })
      res.data.personnes.length > 0 &&
        nextOptions.push({
          key: 'personnes',
          label: 'Copropriétaires',
          options: res.data.personnes.map((p) => ({
            value: p.mail1,
            label: p.nom + ' ' + p.prenom,
            keu: p.id,
          })),
        })
      res.data.fournisseurs.length > 0 &&
        nextOptions.push({
          key: 'fournisseurs',
          label: 'Fournisseurs',
          options: res.data.fournisseurs.map((four) => ({
            value: four.mail1,
            label: four.raisonsociale,
            key: four.id,
          })),
        })

      return Promise.resolve(nextOptions)
    },
    [copro?.trigramme, search]
  )

  const optionsFilter = (fieldName: string) => (options: OptionsType) => {
    return options.map((opt) => ({
      ...opt,
      options: opt.options?.filter((o) => !form.getFieldValue(fieldName)?.includes(o.value)),
    }))
  }

  return (
    <Modal
      title="Envoyer un mail"
      visible={visible}
      footer={false}
      onCancel={onCancel}
      cancelText="Annuler"
      width="66%"
    >
      <Form
        form={form}
        onFinish={handleFinish}
        labelCol={{ span: 2 }}
        initialValues={{
          body: `Bonjour, veuillez trouver en pièce jointe le devis no ${
            fichier?.fichier?.devis_fournisseur?.reference ?? ''
          } de ${fichier?.fichier?.devis_fournisseur?.fournisseur?.raisonsociale ?? ''} d'un montant de ${
            fichier?.fichier?.devis_fournisseur?.montant ?? ''
          }€\n\nSincères Salutations\n${prenom} ${nom}`,
          evt: [fichierId],
          subject: `${copro?.nom} > ${demande.equipementlibelle} > ${demande.description}`.trim() + ` (${demande.id})`,
        }}
      >
        <Form.Item name="to" label="A">
          <DebounceSelect
            placeholder="Recherche"
            mode="tags"
            fetchOptions={handleSearch}
            optionsFilter={optionsFilter('to')}
          />
        </Form.Item>
        <Form.Item name="cc" label="CC">
          <DebounceSelect
            placeholder="Recherche"
            mode="tags"
            fetchOptions={handleSearch}
            optionsFilter={optionsFilter('cc')}
          />
        </Form.Item>
        <Form.Item name="subject" label="Objet">
          <Input />
        </Form.Item>
        <Form.Item name="body" label="Message">
          <Input.TextArea rows={6} />
        </Form.Item>
        <Form.Item wrapperCol={{ offset: 22 }}>
          <Button htmlType="submit" type="primary">
            Envoyer
          </Button>
        </Form.Item>
        <Form.Item name="evt">
          <Checkbox.Group>
            {evenements?.map((evt) => (
              <>
                <Checkbox value={evt.id} key={evt.id} style={{ marginLeft: 0 }}>
                  {evt.fichier ? (
                    <>
                      {evt.texte}
                      <Thumbnail texte={evt.texte} />
                    </>
                  ) : (
                    <div dangerouslySetInnerHTML={{ __html: evt.texte?.trim() ?? '' }} />
                  )}
                </Checkbox>
                <Divider />
              </>
            ))}
          </Checkbox.Group>
        </Form.Item>
        <Form.Item wrapperCol={{ offset: 22 }}>
          <Button htmlType="submit" type="primary">
            Envoyer
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  )
}

export default ModalEnvoyerDevis
